import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  EventAvailable as EventAvailableIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { coachingSessionAPI, userAPI, memberProfileAPI } from '../../services/api';
import { Amenity, User, UserRole, MemberProfile } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { formatDateForInput } from '../../utils/dateUtils';

interface CreateCoachingSessionDialogProps {
  open: boolean;
  onClose: () => void;
  amenity: Amenity | null;
  onSuccess: (message?: string) => void;
  preselectedDate?: Date | null;
  preselectedSlot?: { startTime: string; endTime: string } | null;
}

const CreateCoachingSessionDialog: React.FC<CreateCoachingSessionDialogProps> = ({
  open,
  onClose,
  amenity,
  onSuccess,
  preselectedDate,
  preselectedSlot
}) => {
  const { user } = useAuth();
  const dialogContentRef = React.useRef<HTMLDivElement>(null);
  const [coaches, setCoaches] = useState<User[]>([]);
  const [selectedCoach, setSelectedCoach] = useState<User | null>(null);
  const [sessionName, setSessionName] = useState('');
  const [capacity, setCapacity] = useState(4);
  const [bookingDate, setBookingDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [description, setDescription] = useState('');
  const [level, setLevel] = useState('');  const [minBookingDurationMinutes, setMinBookingDurationMinutes] = useState(30);
  const [memberRate, setMemberRate] = useState({ cost: '0', duration: '' });
  const [guestRate, setGuestRate] = useState({ cost: '0', duration: '' });
  const [pricingConfigExpanded, setPricingConfigExpanded] = useState(false);
  const [enrollmentExpanded, setEnrollmentExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Member enrollment state
  const [memberProfiles, setMemberProfiles] = useState<MemberProfile[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [enrolledMembers, setEnrolledMembers] = useState<Array<{
    id: string;
    member: MemberProfile | null;
    isGuest: boolean;
    guestName: string;
    guestEmail: string;
    startTime: Date;
    endTime: Date;
    overrideCost: boolean;
    overriddenCost: number;
    notes: string;
    timeError?: string;
  }>>([]);
  
  // Check if current user is a coach (coaches can only book for themselves)
  const isCoachUser = user?.role === UserRole.COACH;
  const canChangeCoach = !isCoachUser; // Only non-coaches (admins, etc.) can change coach

  // Fetch coaches when dialog opens
  useEffect(() => {
    if (open) {
      fetchCoaches();
      fetchMembers();
      // Reset form state or populate from preselected values
      setSelectedCoach(null);
      setBookingDate(preselectedDate || null);
      setSessionName('');
      setCapacity(4);
      setLevel('');
      setMinBookingDurationMinutes(30);
      setMemberRate({ cost: '0', duration: '' });
      setGuestRate({ cost: '0', duration: '' });
      setPricingConfigExpanded(false);
      setEnrollmentExpanded(false);
      setEnrolledMembers([]);
      
      // Parse and set times from preselectedSlot if provided
      if (preselectedSlot) {
        const parseTime = (timeString: string): Date => {
          const [hours, minutes] = timeString.substring(0, 5).split(':').map(Number);
          const date = new Date();
          date.setHours(hours, minutes, 0, 0);
          return date;
        };
        
        setStartTime(parseTime(preselectedSlot.startTime));
        setEndTime(parseTime(preselectedSlot.endTime));
        
        // Calculate min booking duration from slot
        const start = parseTime(preselectedSlot.startTime);
        const end = parseTime(preselectedSlot.endTime);
        const duration = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
        setMinBookingDurationMinutes(duration);
      } else {
        setStartTime(null);
        setEndTime(null);
      }
      
      setDescription('');
      setError(null);
      setSuccess(null);
    }
  }, [open, preselectedDate, preselectedSlot, amenity]);

  // Auto-populate session name when coach, amenity, and date are selected
  useEffect(() => {
    if (selectedCoach && amenity && bookingDate) {
      const dateStr = bookingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      setSessionName(`${selectedCoach.firstName} - ${amenity.name} - ${dateStr}`);
    }
  }, [selectedCoach, amenity, bookingDate]);

  // Scroll to top when error occurs
  useEffect(() => {
    if (error && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  // Revalidate enrollment times when session times change
  useEffect(() => {
    if (startTime && endTime && enrolledMembers.length > 0) {
      const updatedMembers = enrolledMembers.map(em => {
        const sessionStart = startTime.getHours() * 60 + startTime.getMinutes();
        const sessionEnd = endTime.getHours() * 60 + endTime.getMinutes();
        const enrollStart = em.startTime.getHours() * 60 + em.startTime.getMinutes();
        const enrollEnd = em.endTime.getHours() * 60 + em.endTime.getMinutes();
        
        let timeError: string | undefined = undefined;
        if (enrollStart < sessionStart || enrollEnd > sessionEnd) {
          timeError = `Time must be within session hours: ${formatTimeForAPI(startTime)} - ${formatTimeForAPI(endTime)}`;
        }
        
        return { ...em, timeError };
      });
      
      // Only update if there are actual changes to avoid infinite loop
      const hasChanges = updatedMembers.some((um, i) => um.timeError !== enrolledMembers[i].timeError);
      if (hasChanges) {
        setEnrolledMembers(updatedMembers);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, endTime]);

  const fetchCoaches = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getUsers({ 
        roles: [UserRole.COACH, UserRole.EMPLOYEE, UserRole.ADMIN, UserRole.SUPER_ADMIN]
      });
      
      // Filter users with specific roles
      const filteredUsers = response.data.filter((u: User) => 
        [UserRole.COACH, UserRole.EMPLOYEE, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(u.role)
      );
      
      setCoaches(filteredUsers);
      
      // Pre-select logged-in coach if user is a coach
      if (isCoachUser && user && !selectedCoach) {
        const loggedInCoach = filteredUsers.find((c: User) => c.id === user.id);
        if (loggedInCoach) {
          setSelectedCoach(loggedInCoach);
        }
      }
    } catch (error: any) {
      console.error('Error fetching coaches:', error);
      setError('Failed to load coaches');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const response = await memberProfileAPI.getAllMemberProfiles();
      // Filter to only active members
      const activeMembers = response.data.filter((member: MemberProfile) => 
        member.status === 'active'
      );
      setMemberProfiles(activeMembers);
    } catch (error: any) {
      console.error('Error fetching members:', error);
      // Don't set error state - member enrollment is optional
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const validateTimeIncrement = (time: Date): string | null => {
    const minutes = time.getMinutes();
    if (minutes % 15 !== 0) {
      return 'Times must be in 15-minute increments (e.g., 9:00, 9:15, 9:30, 9:45)';
    }
    return null;
  };

  const formatTimeForAPI = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Helper function to calculate cost based on duration and guest status
  const calculateSessionCost = (durationMinutes: number, isGuest: boolean) => {
    if (isGuest && guestRate.cost && parseFloat(guestRate.cost) > 0) {
      const duration = parseInt(guestRate.duration) || minBookingDurationMinutes;
      return (durationMinutes / duration) * parseFloat(guestRate.cost);
    } else if (!isGuest && memberRate.cost && parseFloat(memberRate.cost) > 0) {
      const duration = parseInt(memberRate.duration) || minBookingDurationMinutes;
      return (durationMinutes / duration) * parseFloat(memberRate.cost);
    }
    return 0;
  };

  const addEnrolledMember = () => {
    if (!startTime || !endTime) {
      setError('Please set session start and end times first');
      return;
    }

    const defaultEndTime = new Date(startTime.getTime() + minBookingDurationMinutes * 60 * 1000);
    const defaultCost = calculateSessionCost(minBookingDurationMinutes, false);
    const timeError = validateEnrollmentTime(new Date(startTime), defaultEndTime);

    setEnrolledMembers([...enrolledMembers, {
      id: `enrollment-${Date.now()}`,
      member: null,
      isGuest: false,
      guestName: '',
      guestEmail: '',
      startTime: new Date(startTime),
      endTime: defaultEndTime,
      overrideCost: false,
      overriddenCost: defaultCost,
      notes: '',
      timeError
    }]);
  };

  const removeEnrolledMember = (id: string) => {
    setEnrolledMembers(enrolledMembers.filter(em => em.id !== id));
  };

  const updateEnrolledMember = (id: string, updates: Partial<typeof enrolledMembers[0]>) => {
    const updatedMembers = enrolledMembers.map(em => {
      if (em.id === id) {
        const updated = { ...em, ...updates };
        // Validate time range against session times
        updated.timeError = validateEnrollmentTime(updated.startTime, updated.endTime);
        return updated;
      }
      return em;
    });
    setEnrolledMembers(updatedMembers);
  };

  const validateEnrollmentTime = (enrollStartTime: Date, enrollEndTime: Date): string | undefined => {
    if (!startTime || !endTime) return undefined;
    
    // Compare times only (ignore dates)
    const sessionStart = startTime.getHours() * 60 + startTime.getMinutes();
    const sessionEnd = endTime.getHours() * 60 + endTime.getMinutes();
    const enrollStart = enrollStartTime.getHours() * 60 + enrollStartTime.getMinutes();
    const enrollEnd = enrollEndTime.getHours() * 60 + enrollEndTime.getMinutes();
    
    if (enrollStart < sessionStart || enrollEnd > sessionEnd) {
      return `Time must be within session hours: ${formatTimeForAPI(startTime)} - ${formatTimeForAPI(endTime)}`;
    }
    
    return undefined;
  };

  const canOverrideCost = user && (
    user.role === UserRole.SUPER_ADMIN ||
    user.role === UserRole.ADMIN ||
    user.role === UserRole.EMPLOYEE ||
    user.role === UserRole.COACH
  );

  const handleCreateSession = async () => {
    if (!selectedCoach || !amenity || !bookingDate || !startTime || !endTime || !sessionName.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate time increments
    const startTimeError = validateTimeIncrement(startTime);
    const endTimeError = validateTimeIncrement(endTime);
    
    if (startTimeError || endTimeError) {
      setError(startTimeError || endTimeError);
      return;
    }

    // Validate that end time is after start time
    if (endTime <= startTime) {
      setError('End time must be after start time');
      return;
    }

    // Validate that booking is for future date or today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedBookingDate = new Date(bookingDate);
    selectedBookingDate.setHours(0, 0, 0, 0);
    
    if (selectedBookingDate < today) {
      setError('Cannot create coaching sessions for past dates');
      return;
    }

    // Validate enrolled members
    for (let i = 0; i < enrolledMembers.length; i++) {
      const enrollment = enrolledMembers[i];
      
      // Check for time error first
      if (enrollment.timeError) {
        setError(`Enrollment ${i + 1}: ${enrollment.timeError}`);
        return;
      }
      
      if (!enrollment.isGuest && !enrollment.member) {
        setError(`Enrollment ${i + 1}: Please select a member or mark as guest`);
        return;
      }
      
      if (enrollment.isGuest) {
        if (!enrollment.guestName.trim()) {
          setError(`Enrollment ${i + 1}: Guest name is required`);
          return;
        }
        if (!enrollment.guestEmail.trim()) {
          setError(`Enrollment ${i + 1}: Guest email is required`);
          return;
        }
      }
      
      if (enrollment.startTime >= enrollment.endTime) {
        setError(`Enrollment ${i + 1}: End time must be after start time`);
        return;
      }
      
      const enrollmentDuration = Math.floor((enrollment.endTime.getTime() - enrollment.startTime.getTime()) / (1000 * 60));
      if (enrollmentDuration < minBookingDurationMinutes) {
        setError(`Enrollment ${i + 1}: Duration must be at least ${minBookingDurationMinutes} minutes`);
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);

      // Calculate duration in minutes
      const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));

      // Create coaching session data (matching CoachingSessionManagementPage structure)
      const sessionData = {
        name: sessionName.trim(),
        description: description.trim() || undefined,
        coachId: selectedCoach.id,
        isGuestCoach: false,
        venues: [amenity.id], // Single venue reservation
        level: level.trim() || 'Open', // Default to 'Open' if not specified
        capacity: capacity,
        sessionDate: formatDateForInput(bookingDate),
        startTime: formatTimeForAPI(startTime),
        endTime: formatTimeForAPI(endTime),
        minBookingDurationMinutes: minBookingDurationMinutes,
        memberRate: memberRate.cost !== '' && memberRate.cost !== null && memberRate.cost !== undefined && memberRate.cost !== '0' ? {
          cost: parseFloat(memberRate.cost),
          duration: parseInt(memberRate.duration) || minBookingDurationMinutes
        } : undefined,
        guestRate: guestRate.cost !== '' && guestRate.cost !== null && guestRate.cost !== undefined && guestRate.cost !== '0' ? {
          cost: parseFloat(guestRate.cost),
          duration: parseInt(guestRate.duration) || minBookingDurationMinutes
        } : undefined,
        allowGuestBookings: true,
        notes: description.trim() || undefined
      };

      // Create coaching session (this will automatically reserve the venue)
      const sessionResponse = await coachingSessionAPI.createCoachingSession(sessionData);
      
      // Extract session ID - handle different response structures
      const createdSession = sessionResponse.data?.session || sessionResponse.data;
      const sessionId = createdSession?.id;
      
      if (!sessionId) {
        console.error('Session created but ID not found in response:', sessionResponse.data);
        throw new Error('Failed to get created session ID');
      }
      
      // Enroll members if any
      if (enrolledMembers.length > 0) {
        const enrollmentPromises = enrolledMembers.map(async (enrollment) => {
          const enrollmentDuration = Math.floor((enrollment.endTime.getTime() - enrollment.startTime.getTime()) / (1000 * 60));
          const originalCalculatedCost = calculateSessionCost(enrollmentDuration, enrollment.isGuest);
          
          const bookingData = {
            startTime: formatTimeForAPI(enrollment.startTime),
            endTime: formatTimeForAPI(enrollment.endTime),
            durationMinutes: enrollmentDuration,
            memberNotes: enrollment.notes,
            memberProfileId: enrollment.isGuest ? undefined : enrollment.member?.id,
            isGuest: enrollment.isGuest,
            guestName: enrollment.isGuest ? enrollment.guestName : undefined,
            guestEmail: enrollment.isGuest ? enrollment.guestEmail : undefined,
            overrideCost: enrollment.overrideCost,
            overriddenCost: enrollment.overrideCost ? enrollment.overriddenCost : undefined,
            originalCalculatedCost: enrollment.overrideCost ? originalCalculatedCost : undefined
          };
          
          return coachingSessionAPI.bookCoachingSession(sessionId, bookingData);
        });
        
        await Promise.all(enrollmentPromises);
        
        setSuccess(`Coaching session created successfully with ${enrolledMembers.length} member(s) enrolled!`);
      } else {
        setSuccess('Coaching session created successfully!');
      }
      
      setTimeout(() => {
        onSuccess(enrolledMembers.length > 0 
          ? `Coaching session created with ${enrolledMembers.length} member(s) enrolled and venue reserved successfully`
          : 'Coaching session created and venue reserved successfully'
        );
        handleClose();
      }, 1500);
    } catch (error: any) {
      console.error('Error creating coaching session:', error);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to create coaching session. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => {
    return new Date(); // Today
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 6); // 6 months in advance
    return maxDate;
  };

  const formatUserOption = (user: User) => {
    const roleLabel = user.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    return `${user.firstName} ${user.lastName} (${roleLabel})`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={submitting}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EventAvailableIcon sx={{ mr: 1, color: 'primary.main' }} />
            Create Coaching Session
          </Box>
        </DialogTitle>
        
        <DialogContent ref={dialogContentRef}>
          <Box sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            {amenity && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Venue:</strong> {amenity.name}
                  {amenity.location && (
                    <span> - {amenity.location}</span>
                  )}
                </Typography>
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* Session Name */}
              <Grid size={12}>
                <TextField
                  label="Session Name"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  fullWidth
                  required
                  placeholder="e.g., Beginner Coaching - Court 1"
                  helperText="Give this coaching session a descriptive name"
                />
              </Grid>

              {/* Coach Selection */}
              <Grid size={12}>
                <Autocomplete
                  options={coaches}
                  getOptionLabel={(user) => formatUserOption(user)}
                  value={selectedCoach}
                  onChange={(event, newValue) => setSelectedCoach(newValue)}
                  loading={loading}
                  disabled={submitting || !canChangeCoach}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Coach"
                      required
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      helperText={isCoachUser ? "Coaches can only create sessions for themselves" : "Select the coach conducting this session"}
                    />
                  )}
                />
              </Grid>

              {/* Capacity */}
              <Grid size={6}>
                <TextField
                  label="Capacity"
                  type="number"
                  value={capacity}
                  onChange={(e) => setCapacity(parseInt(e.target.value) || 1)}
                  fullWidth
                  required
                  inputProps={{ min: 1, max: 20 }}
                  helperText="Max members"
                />
              </Grid>

              {/* Level */}
              <Grid size={6}>
                <TextField
                  label="Level"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                  fullWidth
                  placeholder="e.g., Beginner, Intermediate, Advanced"
                  helperText="Skill level (defaults to 'Open')"
                />
              </Grid>

              {/* Session Date */}
              <Grid size={12}>
                <DatePicker
                  label="Session Date"
                  value={bookingDate}
                  onChange={setBookingDate}
                  minDate={getMinDate()}
                  maxDate={getMaxDate()}
                  disabled={submitting}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      InputProps: {
                        startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }
                    }
                  }}
                />
              </Grid>

              {/* Start Time */}
              <Grid size={6}>
                <TimePicker
                  label="Start Time"
                  value={startTime}
                  onChange={setStartTime}
                  minutesStep={15}
                  disabled={submitting}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      InputProps: {
                        startAdornment: <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }
                    }
                  }}
                />
              </Grid>

              {/* End Time */}
              <Grid size={6}>
                <TimePicker
                  label="End Time"
                  value={endTime}
                  onChange={setEndTime}
                  minutesStep={15}
                  disabled={submitting}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      InputProps: {
                        startAdornment: <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }
                    }
                  }}
                />
              </Grid>

              {/* Min Booking Duration */}
              <Grid size={6}>
                <TextField
                  label="Minimum Booking Duration (minutes)"
                  type="number"
                  value={minBookingDurationMinutes}
                  onChange={(e) => {
                    const newDuration = parseInt(e.target.value) || 30;
                    setMinBookingDurationMinutes(newDuration);
                    // Update rate durations if they're empty or match the previous min duration
                    if (!memberRate.duration || memberRate.duration === minBookingDurationMinutes.toString()) {
                      setMemberRate({ ...memberRate, duration: newDuration.toString() });
                    }
                    if (!guestRate.duration || guestRate.duration === minBookingDurationMinutes.toString()) {
                      setGuestRate({ ...guestRate, duration: newDuration.toString() });
                    }
                  }}
                  fullWidth
                  inputProps={{ min: 15, max: 240 }}
                  helperText="Minimum time slot members can book"
                />
              </Grid>

  {/* Collapsible Pricing Configuration */}
            <Accordion expanded={pricingConfigExpanded} onChange={() => setPricingConfigExpanded(!pricingConfigExpanded)} sx={{ mt: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Pricing </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Member Rate */}
                  <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Member Rate</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Cost"
                          type="number"
                          value={memberRate.cost}
                          onChange={(e) => setMemberRate({ ...memberRate, cost: e.target.value })}
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          helperText="Cost for members"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Duration (minutes)"
                          type="number"
                          value={memberRate.duration}
                          onChange={(e) => setMemberRate({ ...memberRate, duration: e.target.value })}
                          fullWidth
                          inputProps={{ min: 15, max: 480 }}
                          helperText={`Default: ${minBookingDurationMinutes} min`}
                          placeholder={minBookingDurationMinutes.toString()}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Guest Rate */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" gutterBottom>Guest Rate</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Cost"
                          type="number"
                          value={guestRate.cost}
                          onChange={(e) => setGuestRate({ ...guestRate, cost: e.target.value })}
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          helperText="Cost for guests"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Duration (minutes)"
                          type="number"
                          value={guestRate.duration}
                          onChange={(e) => setGuestRate({ ...guestRate, duration: e.target.value })}
                          fullWidth
                          inputProps={{ min: 15, max: 480 }}
                          helperText={`Default: ${minBookingDurationMinutes} min`}
                          placeholder={minBookingDurationMinutes.toString()}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Member Enrollment Section */}
            <Accordion expanded={enrollmentExpanded} onChange={() => setEnrollmentExpanded(!enrollmentExpanded)} sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  Enroll Members {enrolledMembers.length > 0 && `(${enrolledMembers.length})`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addEnrolledMember}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2 }}
                    disabled={!startTime || !endTime}
                  >
                    Add Member
                  </Button>
                  
                  {!startTime || !endTime ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Set session start and end times first to enable member enrollment
                    </Alert>
                  ) : null}

                  {enrolledMembers.map((enrollment, index) => (
                    <Card key={enrollment.id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2">
                            Enrollment {index + 1}
                          </Typography>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => removeEnrolledMember(enrollment.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        {/* Time Error Alert */}
                        {enrollment.timeError && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {enrollment.timeError}
                          </Alert>
                        )}

                        <Grid container spacing={2}>
                          {/* Guest Toggle */}
                          <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={enrollment.isGuest}
                                  onChange={(e) => {
                                    updateEnrolledMember(enrollment.id, { 
                                      isGuest: e.target.checked,
                                      member: e.target.checked ? null : enrollment.member
                                    });
                                  }}
                                />
                              }
                              label="Guest"
                            />
                          </Grid>

                          {/* Member Selection or Guest Info */}
                          {!enrollment.isGuest ? (
                            <Grid size={{ xs: 12 }}>
                              <Autocomplete
                                options={memberProfiles}
                                value={enrollment.member}
                                onChange={(event, newValue) => {
                                  updateEnrolledMember(enrollment.id, { member: newValue });
                                }}
                                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                                getOptionKey={(option) => option.id}
                                loading={loadingMembers}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props}>
                                    <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                                    <Box>
                                      <Typography variant="body1">
                                        {option.firstName} {option.lastName}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {option.email} • {option.membershipTier?.name || 'No Tier'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Select Member"
                                    placeholder="Search members..."
                                    required
                                    size="small"
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {loadingMembers ? <CircularProgress color="inherit" size={20} /> : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                  />
                                )}
                                noOptionsText={loadingMembers ? "Loading members..." : "No members found"}
                              />
                            </Grid>
                          ) : (
                            <>
                              <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                  label="Guest Name"
                                  value={enrollment.guestName}
                                  onChange={(e) => updateEnrolledMember(enrollment.id, { guestName: e.target.value })}
                                  fullWidth
                                  required
                                  size="small"
                                />
                              </Grid>
                              <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                  label="Guest Email"
                                  type="email"
                                  value={enrollment.guestEmail}
                                  onChange={(e) => updateEnrolledMember(enrollment.id, { guestEmail: e.target.value })}
                                  fullWidth
                                  required
                                  size="small"
                                />
                              </Grid>
                            </>
                          )}

                          {/* Time Selection */}
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TimePicker
                              label="Start Time"
                              value={enrollment.startTime}
                              onChange={(time) => {
                                if (time) {
                                  const newEndTime = new Date(time.getTime() + minBookingDurationMinutes * 60 * 1000);
                                  const duration = Math.floor((newEndTime.getTime() - time.getTime()) / (1000 * 60));
                                  const recalculatedCost = calculateSessionCost(duration, enrollment.isGuest);
                                  
                                  updateEnrolledMember(enrollment.id, { 
                                    startTime: time,
                                    endTime: newEndTime,
                                    overriddenCost: enrollment.overrideCost ? enrollment.overriddenCost : recalculatedCost
                                  });
                                }
                              }}
                              slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <TimePicker
                              label="End Time"
                              value={enrollment.endTime}
                              onChange={(time) => {
                                if (time) {
                                  const duration = Math.floor((time.getTime() - enrollment.startTime.getTime()) / (1000 * 60));
                                  const recalculatedCost = calculateSessionCost(duration, enrollment.isGuest);
                                  
                                  updateEnrolledMember(enrollment.id, { 
                                    endTime: time,
                                    overriddenCost: enrollment.overrideCost ? enrollment.overriddenCost : recalculatedCost
                                  });
                                }
                              }}
                              slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }}
                            />
                          </Grid>

                          {/* Duration and Cost Info */}
                          <Grid size={{ xs: 12 }}>
                            <Box sx={{ p: 1.5, bgcolor: 'info.50', borderRadius: 1 }}>
                              <Typography variant="body2">
                                <strong>Duration:</strong> {Math.floor((enrollment.endTime.getTime() - enrollment.startTime.getTime()) / (1000 * 60))} minutes
                              </Typography>
                              {(memberRate.cost || guestRate.cost) && parseFloat(enrollment.isGuest ? guestRate.cost : memberRate.cost) > 0 && (
                                <Typography variant="body2">
                                  <strong>{enrollment.overrideCost ? 'Original Cost:' : 'Cost:'}</strong> ${calculateSessionCost(
                                    Math.floor((enrollment.endTime.getTime() - enrollment.startTime.getTime()) / (1000 * 60)),
                                    enrollment.isGuest
                                  ).toFixed(2)}
                                </Typography>
                              )}
                            </Box>
                          </Grid>

                          {/* Cost Override */}
                          {canOverrideCost && (memberRate.cost || guestRate.cost) && parseFloat(enrollment.isGuest ? guestRate.cost : memberRate.cost) > 0 && (
                            <>
                              <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={enrollment.overrideCost}
                                      onChange={(e) => {
                                        const isOverriding = e.target.checked;
                                        const duration = Math.floor((enrollment.endTime.getTime() - enrollment.startTime.getTime()) / (1000 * 60));
                                        const currentCalculatedCost = calculateSessionCost(duration, enrollment.isGuest);
                                        
                                        updateEnrolledMember(enrollment.id, { 
                                          overrideCost: isOverriding,
                                          overriddenCost: isOverriding ? enrollment.overriddenCost : currentCalculatedCost
                                        });
                                      }}
                                    />
                                  }
                                  label="Override Cost"
                                />
                              </Grid>
                              {enrollment.overrideCost && (
                                <Grid size={{ xs: 12 }}>
                                  <TextField
                                    label="New Cost"
                                    type="number"
                                    value={enrollment.overriddenCost}
                                    onChange={(e) => {
                                      const value = parseFloat(e.target.value);
                                      updateEnrolledMember(enrollment.id, { 
                                        overriddenCost: isNaN(value) ? 0 : value
                                      });
                                    }}
                                    fullWidth
                                    required
                                    size="small"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                    helperText="Enter a custom cost for this enrollment"
                                  />
                                </Grid>
                              )}
                            </>
                          )}

                          {/* Notes */}
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              label="Notes"
                              value={enrollment.notes}
                              onChange={(e) => updateEnrolledMember(enrollment.id, { notes: e.target.value })}
                              fullWidth
                              multiline
                              rows={2}
                              size="small"
                              placeholder="Any special requests or notes"
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}

                  {enrolledMembers.length === 0 && (
                    <Alert severity="info">
                      No members enrolled yet. Click "Add Member" to enroll members in this session.
                    </Alert>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

              {/* Description */}
              {/* <Grid size={12}>
                <TextField
                  label="Description (Optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  disabled={submitting}
                  placeholder="Add session description, special instructions, or requirements..."
                />
              </Grid> */}
            </Grid>

          

            {/* Session Summary */}
            {selectedCoach && bookingDate && startTime && endTime && sessionName && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Session Summary:
                </Typography>
                <Typography variant="body2">
                  <strong>Session:</strong> {sessionName}
                </Typography>
                <Typography variant="body2">
                  <strong>Coach:</strong> {formatUserOption(selectedCoach)}
                </Typography>
                <Typography variant="body2">
                  <strong>Venue:</strong> {amenity?.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {bookingDate.toLocaleDateString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Time:</strong> {formatTimeForAPI(startTime)} - {formatTimeForAPI(endTime)}
                </Typography>
                <Typography variant="body2">
                  <strong>Duration:</strong> {Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60))} minutes
                </Typography>
                <Typography variant="body2">
                  <strong>Capacity:</strong> {capacity} member{capacity !== 1 ? 's' : ''}
                </Typography>
                {level && (
                  <Typography variant="body2">
                    <strong>Level:</strong> {level}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Min Booking Duration:</strong> {minBookingDurationMinutes} minutes
                </Typography>
                {memberRate.cost && parseFloat(memberRate.cost) > 0 && (
                  <Typography variant="body2">
                    <strong>Member Rate:</strong> ${memberRate.cost} / {memberRate.duration || minBookingDurationMinutes} min
                  </Typography>
                )}
                {guestRate.cost && parseFloat(guestRate.cost) > 0 && (
                  <Typography variant="body2">
                    <strong>Guest Rate:</strong> ${guestRate.cost} / {guestRate.duration || minBookingDurationMinutes} min
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateSession}
            variant="contained"
            disabled={!selectedCoach || !bookingDate || !startTime || !endTime || !sessionName.trim() || submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <EventAvailableIcon />}
          >
            {submitting ? 'Creating...' : 'Create Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateCoachingSessionDialog;
